import { Component, computed, input, linkedSignal } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import type { ChartType } from 'chart.js';

import { WidgetChart, WidgetDelta, WidgetRepresentation } from './dashboard-widget.model';
import { CardHeaderComponent } from './card-header.component';
import { RepSwitcherComponent } from './rep-switcher.component';

@Component({
  selector: 'app-stat-chart-tile',
  standalone: true,
  imports: [ChartModule, CardHeaderComponent, RepSwitcherComponent],
  template: `
    <div
      class="card flex h-full flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 dark:border-surface-700/40 dark:bg-surface-900"
    >
      <app-card-header [title]="title()" [subtitle]="subtitle()" [icon]="icon()" [accent]="accent()">
        <app-rep-switcher
          header-actions
          [reps]="styleOptions()"
          [active]="style()"
          (repChange)="style.set($event)"
        />
      </app-card-header>

      <div class="flex flex-1 items-center gap-4">
        <div class="flex shrink-0 flex-col">
          @if (loading()) {
          <span class="h-8 w-20 animate-pulse rounded bg-surface-200 dark:bg-surface-700"></span>
          } @else {
          <div class="flex items-baseline gap-1">
            <span class="text-3xl font-extrabold tracking-tight text-text-main">{{ value() }}</span>
            @if (max() != null) {
            <span class="text-base font-semibold text-text-muted">/ {{ max() }}</span>
            }
            @if (unit()) {
            <span class="text-sm font-medium text-text-muted">{{ unit() }}</span>
            }
          </div>
          @if (delta(); as d) {
          <span class="mt-1 flex items-center gap-1 text-xs font-medium" [class]="deltaClass(d)">
            <i [class]="d.direction === 'up' ? 'pi pi-arrow-up' : 'pi pi-arrow-down'" class="text-[0.6rem]"></i>
            <span>{{ d.value }}</span>
          </span>
          }
          }
        </div>

        <div class="min-w-0 flex-1">
          @if (loading()) {
          <div class="h-16 w-full animate-pulse rounded-lg bg-surface-100 dark:bg-surface-800"></div>
          } @else if (chart()) {
          <p-chart [type]="chartJsType()" [data]="data()" [options]="options()" [style]="{ height: '5rem' }" />
          }
        </div>
      </div>
    </div>
  `,
})
export class StatChartTileComponent {
  title = input.required<string>();
  value = input.required<string | number>();
  max = input<number | undefined>(undefined);
  unit = input<string>('');
  subtitle = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  chart = input<WidgetChart | undefined>(undefined);
  delta = input<WidgetDelta | undefined>(undefined);
  loading = input<boolean>(false);
  /** Default inner chart style, and the styles offered in the switcher. */
  defaultStyle = input<WidgetRepresentation>('bar');
  styleOptions = input<WidgetRepresentation[]>([]);

  /** Active chart style — starts at the default, user can override. */
  style = linkedSignal<WidgetRepresentation>(() => this.defaultStyle());

  private static readonly PALETTE = ['#3b82f6', '#f59e0b', '#14b8a6', '#ef4444', '#8b5cf6', '#22c55e', '#64748b'];
  private static readonly HUE = '#3b82f6';
  private static readonly GRID = 'rgba(148, 163, 184, 0.18)';
  private static readonly INK = '#94a3b8';

  private partToWhole = computed(() => this.style() === 'pie' || this.style() === 'donut');

  chartJsType = computed<ChartType>(() => {
    const s = this.style();
    if (s === 'donut') return 'doughnut';
    return (s === 'pie' ? 'pie' : s) as ChartType;
  });

  data = computed(() => {
    const c = this.chart();
    if (!c) return { labels: [], datasets: [] };

    if (this.partToWhole()) {
      return {
        labels: c.labels,
        datasets: [
          {
            data: c.datasets[0]?.data ?? [],
            backgroundColor: c.labels.map((_, i) => StatChartTileComponent.PALETTE[i % 7]),
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      };
    }

    const asLine = this.style() === 'line';
    return {
      labels: c.labels,
      // One series → the single brand hue; multiple series → distinct palette
      // colours so they can be told apart (e.g. hires vs leavers).
      datasets: c.datasets.map((d, i) => {
        const hue = StatChartTileComponent.PALETTE[i % 7];
        return {
          label: d.label,
          data: d.data,
          borderColor: hue,
          backgroundColor: asLine ? hue + '22' : hue,
          borderWidth: asLine ? 2 : 0,
          borderRadius: asLine ? 0 : 4,
          fill: asLine,
          tension: 0.35,
          pointRadius: asLine ? 2 : 0,
        };
      }),
    };
  });

  /** Show the legend only when more than one series shares the chart. */
  private multiSeries = computed(() => (this.chart()?.datasets.length ?? 0) > 1);

  options = computed(() => {
    if (this.partToWhole()) {
      return {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: { color: StatChartTileComponent.INK, usePointStyle: true, boxWidth: 8, font: { size: 10 } },
          },
        },
      };
    }
    return {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: this.multiSeries()
          ? { display: true, position: 'top', labels: { color: StatChartTileComponent.INK, usePointStyle: true, boxWidth: 8, font: { size: 10 } } }
          : { display: false },
        tooltip: { enabled: true },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: StatChartTileComponent.INK, font: { size: 10 } } },
        y: {
          beginAtZero: true,
          grid: { color: StatChartTileComponent.GRID },
          ticks: { color: StatChartTileComponent.INK, font: { size: 10 }, maxTicksLimit: 4 },
        },
      },
    };
  });

  deltaClass(d: WidgetDelta): string {
    switch (d.sentiment) {
      case 'good':
        return 'text-green-600 dark:text-green-400';
      case 'bad':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-text-muted';
    }
  }
}
